import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ELEMENTS = [
  { key: "global.btn_primary", label: "Boutons principaux", group: "Tout le site" },
  { key: "global.btn_outline", label: "Boutons secondaires", group: "Tout le site" },
  { key: "global.btn_gold", label: "Boutons dorés", group: "Tout le site" },
  { key: "global.form_input", label: "Champs de formulaire", group: "Tout le site" },
  { key: "univers.card", label: "Cartes des activités (portail)", group: "Univers Maasaï" },
  { key: "palais.hero_cta", label: "Bouton « Réserver » de l'accueil", group: "Palais Maasaï" },
  { key: "comptoir.product_card", label: "Cartes produits", group: "Le Comptoir" },
  { key: "comptoir.add_button", label: "Boutons « Ajouter au panier »", group: "Le Comptoir" },
  { key: "services.service_card", label: "Cartes prestations", group: "Maasaï Services" },
  { key: "services.book_button", label: "Boutons « Réserver » (prestations)", group: "Maasaï Services" },
];

const SHADOWS = [
  { value: "none", label: "Aucune" },
  { value: "sm", label: "Légère" },
  { value: "md", label: "Moyenne" },
  { value: "lg", label: "Forte" },
];

const HOVERS = [
  { value: "none", label: "Aucun" },
  { value: "lift", label: "Surélévation" },
  { value: "glow", label: "Halo lumineux" },
  { value: "darken", label: "Assombrissement" },
];

const ANIMATIONS = [
  { value: "none", label: "Aucune" },
  { value: "pulse", label: "Pulsation" },
  { value: "float", label: "Flottement" },
];

const emptyStyle = {
  color: "",
  background: "",
  gradient: false,
  gradient_from: "#C4704F",
  gradient_to: "#A85B3D",
  border_color: "",
  border_width: "",
  radius: "",
  shadow: "none",
  opacity: "",
  hover: "",
  hover_color: "",
  hover_background: "",
  animation: "",
};

export default function ElementStylesManager() {
  const [design, setDesign] = useState(null);
  const [selected, setSelected] = useState(ELEMENTS[0].key);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setDesign(r.data?.design || {}))
      .catch(() => setDesign({}));
  }, []);

  if (!design) return null;

  const styles = design.element_styles || {};
  const current = { ...emptyStyle, ...(styles[selected] || {}) };
  const groups = [...new Set(ELEMENTS.map((e) => e.group))];

  const update = (key, value) => {
    setDesign((d) => ({
      ...d,
      element_styles: {
        ...(d.element_styles || {}),
        [selected]: { ...emptyStyle, ...(d.element_styles?.[selected] || {}), [key]: value },
      },
    }));
  };

  const reset = () => {
    setDesign((d) => {
      const next = { ...(d.element_styles || {}) };
      delete next[selected];
      return { ...d, element_styles: next };
    });
    toast.success("Style de l'élément réinitialisé.");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { design });
      toast.success("Styles enregistrés. Rechargez le site pour les voir.");
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const previewStyle = {
    color: current.color || undefined,
    background: current.gradient
      ? `linear-gradient(135deg,${current.gradient_from},${current.gradient_to})`
      : current.background || undefined,
    borderColor: current.border_color || undefined,
    borderWidth: current.border_width ? `${current.border_width}px` : undefined,
    borderStyle: current.border_width ? "solid" : undefined,
    borderRadius: current.radius || undefined,
    opacity: current.opacity || undefined,
    boxShadow:
      current.shadow === "sm"
        ? "0 2px 8px rgba(0,0,0,0.3)"
        : current.shadow === "md"
          ? "0 8px 24px rgba(0,0,0,0.45)"
          : current.shadow === "lg"
            ? "0 16px 48px rgba(0,0,0,0.6)"
            : undefined,
  };

  return (
    <div data-testid="element-styles-manager">
      <h2 className="mb-2 text-lg font-semibold text-ivory">Styles par élément</h2>
      <p className="mb-8 max-w-2xl text-xs leading-relaxed text-ivory/40">
        Personnalisez chaque élément indépendamment : couleur, fond, dégradé, bordure, ombre,
        arrondi, transparence, effet au survol et animation.
      </p>

      <div className="grid gap-8 lg:grid-cols-[300px_1fr]">
        <div className="space-y-6" data-testid="element-list">
          {groups.map((group) => (
            <div key={group}>
              <p className="overline mb-3 !text-[10px]">{group}</p>
              <div className="space-y-1.5">
                {ELEMENTS.filter((e) => e.group === group).map((e) => (
                  <button
                    key={e.key}
                    onClick={() => setSelected(e.key)}
                    data-testid={`element-select-${e.key}`}
                    className={`w-full rounded-md border px-4 py-2.5 text-left text-xs transition-colors ${
                      selected === e.key
                        ? "border-copper bg-copper/15 text-ivory"
                        : "border-white/10 text-ivory/60 hover:border-copper/40 hover:text-ivory"
                    }`}
                  >
                    {e.label}
                    {styles[e.key] && <span className="ml-2 text-gold">●</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-6 rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="element-editor">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ivory">
              {ELEMENTS.find((e) => e.key === selected)?.label}
            </h3>
            {styles[selected] && (
              <button onClick={reset} className="flex items-center gap-2 text-xs text-ivory/40 transition-colors hover:text-red-400" data-testid="element-reset-button">
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                Réinitialiser
              </button>
            )}
          </div>

          <div className="rounded-lg border border-white/10 bg-ink p-6" data-testid="element-preview">
            <span
              className="inline-flex items-center justify-center px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.15em] text-ivory"
              style={{ ...previewStyle, ...(!current.background && !current.gradient ? { backgroundColor: "#C4704F" } : {}), borderRadius: current.radius || "0.5rem" }}
            >
              Aperçu de l'élément
            </span>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="flex items-center gap-3">
              <input type="color" value={current.color || "#F5F5F0"} onChange={(e) => update("color", e.target.value)} className="h-10 w-12 cursor-pointer rounded-md border border-white/10 bg-white/5 p-1" data-testid="element-color" />
              <p className="text-xs text-ivory/60">Couleur du texte</p>
            </div>
            <div className="flex items-center gap-3">
              <input type="color" value={current.background || "#C4704F"} onChange={(e) => update("background", e.target.value)} className="h-10 w-12 cursor-pointer rounded-md border border-white/10 bg-white/5 p-1" data-testid="element-background" />
              <p className="text-xs text-ivory/60">Couleur de fond</p>
            </div>
          </div>

          <div>
            <label className="flex items-center gap-3 text-xs text-ivory/60">
              Dégradé
              <Switch checked={current.gradient} onCheckedChange={(v) => update("gradient", v)} data-testid="element-gradient-switch" />
            </label>
            {current.gradient && (
              <div className="mt-3 flex items-center gap-4">
                <input type="color" value={current.gradient_from} onChange={(e) => update("gradient_from", e.target.value)} className="h-10 w-12 cursor-pointer rounded-md border border-white/10 bg-white/5 p-1" data-testid="element-gradient-from" />
                <span className="text-xs text-ivory/40">→</span>
                <input type="color" value={current.gradient_to} onChange={(e) => update("gradient_to", e.target.value)} className="h-10 w-12 cursor-pointer rounded-md border border-white/10 bg-white/5 p-1" data-testid="element-gradient-to" />
              </div>
            )}
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="flex items-center gap-3">
              <input type="color" value={current.border_color || "#C6A87C"} onChange={(e) => update("border_color", e.target.value)} className="h-10 w-12 cursor-pointer rounded-md border border-white/10 bg-white/5 p-1" data-testid="element-border-color" />
              <p className="text-xs text-ivory/60">Couleur de bordure</p>
            </div>
            <div>
              <label className="mb-1.5 block text-xs text-ivory/60">
                Épaisseur bordure — {current.border_width || 0} px
              </label>
              <input type="range" min="0" max="6" step="0.5" value={parseFloat(current.border_width) || 0} onChange={(e) => update("border_width", e.target.value)} className="w-full accent-[#C4704F]" data-testid="element-border-width" />
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs text-ivory/60">Arrondi — {current.radius || "défaut"}</label>
              <input type="range" min="0" max="2" step="0.05" value={parseFloat(current.radius) || 0} onChange={(e) => update("radius", `${e.target.value}rem`)} className="w-full accent-[#C4704F]" data-testid="element-radius" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs text-ivory/60">
                Transparence — {current.opacity ? Math.round(parseFloat(current.opacity) * 100) : 100} %
              </label>
              <input type="range" min="0.2" max="1" step="0.05" value={parseFloat(current.opacity) || 1} onChange={(e) => update("opacity", e.target.value)} className="w-full accent-[#C4704F]" data-testid="element-opacity" />
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-xs text-ivory/60">Ombre</label>
              <Select value={current.shadow} onValueChange={(v) => update("shadow", v)}>
                <SelectTrigger className="!h-10 border-white/10 bg-white/5 text-xs text-ivory" data-testid="element-shadow">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-coal text-ivory">
                  {SHADOWS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs text-ivory/60">Effet au survol</label>
              <Select value={current.hover || "none"} onValueChange={(v) => update("hover", v === "none" ? "" : v)}>
                <SelectTrigger className="!h-10 border-white/10 bg-white/5 text-xs text-ivory" data-testid="element-hover">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-coal text-ivory">
                  {HOVERS.map((h) => (
                    <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs text-ivory/60">Animation</label>
              <Select value={current.animation || "none"} onValueChange={(v) => update("animation", v === "none" ? "" : v)}>
                <SelectTrigger className="!h-10 border-white/10 bg-white/5 text-xs text-ivory" data-testid="element-animation">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-coal text-ivory">
                  {ANIMATIONS.map((a) => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <button onClick={save} disabled={saving} className="btn-primary" data-testid="element-save-button">
            {saving ? "Enregistrement…" : "Enregistrer les styles"}
          </button>
        </div>
      </div>
    </div>
  );
}
